import { Board } from "./board";
import { BOARD_COLS, BOARD_ROWS } from "./constants";
import type { Piece, Square } from "./types";

function sameSq(a: Square, b: Square): boolean {
  return a.row === b.row && a.col === b.col;
}

function add(sq: Square, dr: number, dc: number): Square {
  return { row: sq.row + dr, col: sq.col + dc };
}

function pushSlider(board: Board, from: Square, piece: Piece, dirs: ReadonlyArray<[number, number]>, out: Square[]): void {
  for (const [dr, dc] of dirs) {
    let s = add(from, dr, dc);
    while (board.inBounds(s)) {
      const occ = board.get(s);
      if (!occ) {
        out.push(s);
      } else {
        if (occ.side !== piece.side) out.push(s);
        break;
      }
      s = add(s, dr, dc);
    }
  }
}

const ROOK_DIRS: ReadonlyArray<[number, number]> = [[-1, 0], [1, 0], [0, -1], [0, 1]];
const BISHOP_DIRS: ReadonlyArray<[number, number]> = [[-1, -1], [-1, 1], [1, -1], [1, 1]];
const QUEEN_DIRS: ReadonlyArray<[number, number]> = [...ROOK_DIRS, ...BISHOP_DIRS];
const KNIGHT_JUMPS: ReadonlyArray<[number, number]> = [
  [-2, -1], [-2, 1], [-1, -2], [-1, 2], [1, -2], [1, 2], [2, -1], [2, 1],
];

/**
 * Legal destination squares for a piece at `from`.
 * MVP rules: standard movement and captures. No castling, no en passant, no check tests.
 * Freshly spawned pieces (summoning sickness) may move but not capture.
 * Pawn direction: player pawns move "up" (toward row 0). Enemy pawns move "down".
 * Pawn promotion: simple auto-queen on reaching the last rank.
 */
export function legalMoves(board: Board, from: Square): Square[] {
  const piece = board.get(from);
  if (!piece) return [];
  const out: Square[] = [];

  switch (piece.type) {
    case "pawn": {
      const dir = piece.side === "player" ? -1 : 1;
      // Forward 1
      const f1 = add(from, dir, 0);
      if (board.inBounds(f1) && !board.get(f1)) out.push(f1);
      // Diagonal captures
      for (const dc of [-1, 1]) {
        const d = add(from, dir, dc);
        if (board.inBounds(d)) {
          const occ = board.get(d);
          if (occ && occ.side !== piece.side) out.push(d);
        }
      }
      break;
    }
    case "knight": {
      for (const [dr, dc] of KNIGHT_JUMPS) {
        const s = add(from, dr, dc);
        if (!board.inBounds(s)) continue;
        const occ = board.get(s);
        if (!occ || occ.side !== piece.side) out.push(s);
      }
      break;
    }
    case "bishop":
      pushSlider(board, from, piece, BISHOP_DIRS, out);
      break;
    case "rook":
      pushSlider(board, from, piece, ROOK_DIRS, out);
      break;
    case "queen":
      pushSlider(board, from, piece, QUEEN_DIRS, out);
      break;
    case "king": {
      for (const [dr, dc] of QUEEN_DIRS) {
        const s = add(from, dr, dc);
        if (!board.inBounds(s)) continue;
        const occ = board.get(s);
        if (!occ || occ.side !== piece.side) out.push(s);
      }
      break;
    }
  }

  // Deduplicate (defensive).
  const deduped = out.filter((sq, i, a) => a.findIndex((x) => sameSq(x, sq)) === i);
  // Summoning sickness: freshly spawned pieces may move but not capture.
  return piece.summonSick ? deduped.filter((sq) => !board.get(sq)) : deduped;
}

/**
 * Apply a move. Returns the captured piece (if any). Handles auto-queen promotion.
 */
export function applyMove(board: Board, from: Square, to: Square): Piece | null {
  const piece = board.get(from);
  if (!piece) return null;
  const captured = board.get(to);
  board.place(from, null);

  let moved = piece;
  if (piece.type === "pawn") {
    const lastRank = piece.side === "player" ? 0 : BOARD_ROWS - 1;
    if (to.row === lastRank) {
      moved = { ...piece, type: "queen" };
    }
  }
  board.place(to, moved);
  return captured;
}

export function isInBoardCols(col: number): boolean {
  return col >= 0 && col < BOARD_COLS;
}
